import { platformUrl } from './utils/platformDomain';
import { LanguageProvider } from './contexts/LanguageProvider';
import "@/App.css";
import { useEffect } from "react";
import { lazyPage, PageLoading } from "./components/RoutePage";
import { AppearanceProvider, useAppearanceSnapshot } from './hooks/useAppearance';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { OwnerAuthProvider, useOwnerAuth } from "./contexts/OwnerAuthContext";
import { TenantProvider, useTenant } from "./contexts/TenantContext";
import PlatformIdentityProvider from './contexts/PlatformIdentityContext';
import { Toaster } from "./components/ui/sonner";

// Auth Pages
const Login = lazyPage(() => import("./pages/Login"));
const Register = lazyPage(() => import("./pages/Register"));
const AdminLogin = lazyPage(() => import("./pages/AdminLogin"));
const ForgotPassword = lazyPage(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyPage(() => import("./pages/ResetPassword"));

// Platform Pages (Main Domain)
const LandingPage = lazyPage(() => import("./pages/LandingPage"));
const PublishedContent = lazyPage(() => import("./pages/PublishedContent"));
const TenantLanding = lazyPage(() => import("./pages/TenantLanding"));
const FeaturesPage = lazyPage(() => import("./pages/FeaturesPage"));
const PricingPage = lazyPage(() => import("./pages/PricingPage"));
const BlogPage = lazyPage(() => import("./pages/BlogPage"));
const DemoPage = lazyPage(() => import("./pages/DemoPage"));
const UpdatesPage = lazyPage(() => import("./pages/UpdatesPage"));
const Panels = lazyPage(() => import("./pages/Panels"));
const OwnerPlaceholder = lazyPage(() => import("./pages/owner/OwnerPlaceholder"));
const OwnerSettings = lazyPage(() => import("./pages/owner/OwnerSettings"));
const OwnerSupport = lazyPage(() => import("./pages/owner/OwnerSupport"));
const OwnerWhatsNew = lazyPage(() => import("./pages/owner/OwnerWhatsNew"));
const OwnerReferrals = lazyPage(() => import("./pages/owner/OwnerReferrals"));
const OwnerChildPanels = lazyPage(() => import("./pages/owner/OwnerChildPanels"));
const OwnerPanelStaff = lazyPage(() => import("./pages/owner/OwnerPanelStaff"));
const OwnerPanelActivity = lazyPage(() => import("./pages/owner/OwnerPanelActivity"));
const OwnerPanelAdminApi = lazyPage(() => import("./pages/owner/OwnerPanelAdminApi"));
const OwnerGateways = lazyPage(() => import("./pages/owner/OwnerGateways"));
const OwnerParsingCredits = lazyPage(() => import("./pages/owner/OwnerParsingCredits"));
const OwnerHostedEmail = lazyPage(() => import("./pages/owner/OwnerHostedEmail"));
const OwnerDomains = lazyPage(() => import("./pages/owner/OwnerDomains"));
const OwnerPanelDomain = lazyPage(() => import("./pages/owner/OwnerPanelDomain"));
const OwnerPanelDns = lazyPage(() => import("./pages/owner/OwnerPanelDns"));
const OwnerSignup = lazyPage(() => import("./pages/owner/OwnerSignup"));
const OwnerLogin = lazyPage(() => import("./pages/owner/OwnerLogin"));
const OwnerForgotPassword = lazyPage(() => import("./pages/owner/OwnerForgotPassword"));
const OwnerResetPassword = lazyPage(() => import("./pages/owner/OwnerResetPassword"));
const OrderPanel = lazyPage(() => import("./pages/owner/OrderPanel"));
const OwnerVerifyEmail = lazyPage(() => import("./pages/owner/OwnerVerifyEmail"));
const OwnerInvoices = lazyPage(() => import("./pages/owner/OwnerInvoices"));
const OwnerWallet = lazyPage(() => import("./pages/owner/OwnerWallet"));
const OwnerInvoiceDetail = lazyPage(() => import("./pages/owner/OwnerInvoiceDetail"));
const SuperAdminLogin = lazyPage(() => import("./pages/SuperAdminLogin"));
const SuperAdminDashboard = lazyPage(() => import("./pages/superadmin/SuperAdminDashboard"));
const SuperAdminPanelDetail = lazyPage(() => import("./pages/superadmin/SuperAdminPanelDetail"));
const SuperAdminTenantUsers = lazyPage(() => import("./pages/superadmin/SuperAdminTenantUsers"));
const PanelNotActive = lazyPage(() => import("./pages/PanelNotActive"));
const CustomerVerifyEmail = lazyPage(() => import("./pages/CustomerVerifyEmail"));
const VerifyEmail = lazyPage(() => import("./pages/VerifyEmail"));

// User Pages
const Dashboard = lazyPage(() => import("./pages/Dashboard"));
const NewOrder = lazyPage(() => import("./pages/NewOrder"));
const MassOrder = lazyPage(() => import("./pages/MassOrder"));
const Services = lazyPage(() => import("./pages/Services"));
const OrderHistory = lazyPage(() => import("./pages/OrderHistory"));
const Subscriptions = lazyPage(() => import("./pages/Subscriptions"));
const ImportedHistory = lazyPage(() => import("./pages/ImportedHistory"));
const Payments = lazyPage(() => import("./pages/Payments"));
const Tickets = lazyPage(() => import("./pages/Tickets"));
const Settings = lazyPage(() => import("./pages/Settings"));
const APIPage = lazyPage(() => import("./pages/APIPage").then(module => ({ default: module.PublishedApiPage })));
const Affiliates = lazyPage(() => import("./pages/Affiliates"));
const ChildPanel = lazyPage(() => import("./pages/ChildPanel"));

// Admin Pages
const AdminDashboard = lazyPage(() => import("./pages/admin/AdminDashboard"));
const AdminAccount = lazyPage(() => import("./pages/admin/AdminAccount"));
const AdminSettingsTools = lazyPage(() => import("./pages/admin/AdminSettingsTools"));
const AdminUsers = lazyPage(() => import("./pages/admin/AdminUsers"));
const AdminServices = lazyPage(() => import("./pages/admin/AdminServices"));
const AdminOrders = lazyPage(() => import("./pages/admin/AdminOrders"));
const AdminProviders = lazyPage(() => import("./pages/admin/AdminProviders"));
const AdminTickets = lazyPage(() => import("./pages/admin/AdminTickets"));
const AdminPayments = lazyPage(() => import("./pages/admin/AdminPayments"));
const AdminSettings = lazyPage(() => import("./pages/admin/AdminSettings"));
const AdminAutoLikesSettings = lazyPage(() => import("./pages/admin/AdminAutoLikesSettings"));
const AdminExportUsers = lazyPage(() => import("./pages/admin/AdminExportUsers"));
const AdminDripFeed = lazyPage(() => import("./pages/admin/AdminDripFeed"));
const AdminReports = lazyPage(() => import("./pages/admin/AdminReports"));
const LastUpdates = lazyPage(() => import("./pages/LastUpdates"));
const AdminLastUpdates = lazyPage(() => import("./pages/admin/AdminLastUpdates"));
const AdminOffers = lazyPage(() => import("./pages/admin/AdminOffers"));
const AdminAppearance = lazyPage(() => import("./pages/admin/AdminAppearance"));
const AdminPageEditor = lazyPage(() => import("./pages/admin/AdminPageEditor"));
const AdminBlogEditor = lazyPage(() => import("./pages/admin/AdminBlogEditor"));
const AdminThemeEditor = lazyPage(() => import("./pages/admin/AdminThemeEditor"));
const AdminSubscriptions = lazyPage(() => import("./pages/admin/AdminSubscriptions"));
const AdminRefill = lazyPage(() => import("./pages/admin/AdminRefill"));
const AdminCancel = lazyPage(() => import("./pages/admin/AdminCancel"));
const AdminAffiliates = lazyPage(() => import("./pages/admin/AdminAffiliates"));
const AdminChildPanels = lazyPage(() => import("./pages/admin/AdminChildPanels"));

// The platform auth/user area lives on my.justanotherpanels.com; the bare domain is marketing-only.
const PLATFORM_URL = platformUrl();

const getHostname = () =>
  typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";

// Marketing domain only (bare + www). Deliberately NOT isMainDomain from TenantContext:
// that is also true on my.*, which would make the redirects below loop forever.
const isMarketingDomain = () => {
  const host = getHostname();
  return ["justanotherpanels.com", "www.justanotherpanels.com", "smmclone.com", "www.smmclone.com"].includes(host);
};

const isPlatformSubdomain = () => ["my.justanotherpanels.com", "my.smmclone.com"].includes(getHostname());

// <Navigate> only handles in-app paths, so cross-origin needs a real page load.
const ExternalRedirect = ({ to }) => {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
};

// Where each RBAC section lives, for bouncing staff to a section they hold.
const PERMISSION_PATHS = {
  dashboard: '/sadmin',
  users: '/sadmin/users',
  orders: '/sadmin/orders',
  services: '/sadmin/services',
  providers: '/sadmin/providers',
  payments: '/sadmin/payments',
  tickets: '/sadmin/tickets',
  affiliates: '/sadmin/affiliates',
  child_panels: '/sadmin/child-panels',
  appearance: '/sadmin/appearance',
  'appearance.pages': '/sadmin/offers',
};

function TenantCustomerAppearance({ children }) {
  const { isMainDomain, isLoading } = useTenant();
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const customer = isMainDomain === false
    && !/^\/(sadmin|admin|owner|superadmin|super-admin|my|panels|order-panel|create-panel|signup)(\/|$)/i.test(pathname)
    && (!user || ['user', 'reseller'].includes(user.role));
  return <AppearanceProvider customer={customer} enabled={customer && !isLoading && !loading}>{children}</AppearanceProvider>;
}

function CustomerLoadingScreen() {
  const appearance = useAppearanceSnapshot();
  if (appearance?.customer) return <PageLoading />;
  return <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-violet-600/30 border-t-violet-600 rounded-full animate-spin" />
  </div>;
}

// Protected Route Component.
// adminOnly: admins only (settings, reports, exports...).
// permission: admins always pass; staff pass if they hold that section
// permission (mirrors require_permission on the backend).
const ProtectedRoute = ({ children, adminOnly = false, staffOnly = false, permission = null }) => {
  const { user, loading, token, isAdmin, isStaff, hasPermission, permissionsLoaded, permissions } = useAuth();
  const location = useLocation();

  if (loading) {
    return <CustomerLoadingScreen />;
  }

  if (!token) {
    return <Navigate to={location.pathname.startsWith('/sadmin') ? '/sadmin' : '/login'} replace />;
  }

  // Non-admin trying to access admin routes → redirect to customer order entry
  if (adminOnly && !isAdmin) {
    return <Navigate to="/order" replace />;
  }
  if (staffOnly && !isStaff) return <Navigate to="/order" replace />;

  if (permission) {
    if (!isStaff) {
      return <Navigate to="/order" replace />;
    }
    if (!isAdmin && !permissionsLoaded) {
      return (
        <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      );
    }
    if (!hasPermission(permission)) {
      // Bounce staff to the first section they DO hold; if they hold none
      // (or we are already there), show a dead-end instead of a loop.
      const fallback = (permissions || [])
        .map((p) => PERMISSION_PATHS[p])
        .find(Boolean);
      if (fallback && fallback !== location.pathname) {
        return <Navigate to={fallback} replace />;
      }
      return (
        <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-xl font-semibold text-gray-900 mb-2">No access</h1>
            <p className="text-sm text-gray-500">
              Your staff account does not have permission for this section.
            </p>
          </div>
        </div>
      );
    }
  }

  return children;
};

// Owner Route - the owner dashboard exists only on the platform subdomain.
// On tenant subdomains and the marketing domain these paths fall through to "/".
const OwnerSpinner = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
  </div>
);

const OwnerRoute = ({ children }) => {
  const { owner, token, loading } = useOwnerAuth();

  if (!isPlatformSubdomain()) {
    return <Navigate to="/" replace />;
  }

  // A token on its own is not a session. Every owner page calls the API, so
  // rendering the layout before /owner/me resolves shows the sidebar wrapped
  // around an auth error instead of sending the visitor to the login page.
  // OwnerAuthContext clears the token when /owner/me fails, so this cannot hang.
  if (loading || (token && !owner)) {
    return <OwnerSpinner />;
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Owner-area login/signup pages: send an already-signed-in owner to their panels.
const OwnerPublicRoute = ({ children }) => {
  const { owner, token, loading } = useOwnerAuth();

  if (loading || (token && !owner)) {
    return <OwnerSpinner />;
  }

  if (token && owner) {
    return <Navigate to="/panels" replace />;
  }

  return children;
};

// Public Route Component (redirect based on role if logged in)
const PublicRoute = ({ children }) => {
  const { token, loading, user, isStaff } = useAuth();
  const { isMainDomain, isLoading: tenantLoading } = useTenant();

  if (loading || tenantLoading) {
    return <CustomerLoadingScreen />;
  }

  // Redirect logged-in users based on role. On the platform subdomain there is no
  // tenant behind the panel routes, so owners land on their panels list instead.
  // Staff belong in the admin area like admins; their sections are gated there.
  if (token && user) {
    if (isPlatformSubdomain()) {
      return <Navigate to="/panels" replace />;
    }
    return <Navigate to={isStaff ? '/sadmin' : '/dashboard'} replace />;
  }

  return children;
};

// Platform Route - Only accessible on main domain
const PlatformRoute = ({ children, marketingOnly = false }) => {
  const { isMainDomain, isLoading } = useTenant();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-600/30 border-t-violet-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Marketing pages must never render platform branding on a tenant host.
  // Keep existing verification and privileged route guards unchanged.
  if (marketingOnly && !isMainDomain) return <Navigate to="/" replace />;

  return children;
};

// Tenant Route - Only accessible on tenant subdomain with active status
const TenantRoute = ({ children }) => {
  const { tenant, isMainDomain, isLoading, error } = useTenant();

  if (isLoading) {
    return <CustomerLoadingScreen />;
  }

  // Show error if tenant not found
  if (error && !isMainDomain) {
    return <PanelNotActive />;
  }

  // Show pending/suspended page for non-active tenants
  if (tenant && !tenant.is_active && !isMainDomain) {
    return <PanelNotActive />;
  }

  return children;
};

// The canonical panel-admin entry point serves both login and dashboard.
// This keeps the browser on /sadmin after a password login or owner-code
// exchange instead of bouncing through the legacy /admin route.
const AdminEntryRoute = () => {
  const { token, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-600/30 border-t-violet-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!token) return <AdminLogin />;
  return <ProtectedRoute permission="dashboard"><AdminDashboard /></ProtectedRoute>;
};

function AppRoutes() {
  const { isMainDomain, isLoading } = useTenant();

  if (isLoading) {
    return <CustomerLoadingScreen />;
  }

  return (
    <Routes>
      {/* Platform Routes (Main Domain Only) */}
      <Route path="/" element={
        isPlatformSubdomain()
          ? <Navigate to="/login" replace />
          : isMainDomain ? <LandingPage /> : <TenantRoute><TenantLanding /></TenantRoute>
      } />
      {/* Legacy panel-creation entry point. Panels are now ordered from inside
          the owner dashboard, so this only forwards; /api/tenant/register still
          exists but nothing in the UI links to it. */}
      <Route path="/create-panel" element={<Navigate to="/order-panel" replace />} />
      {/* Owner area (platform subdomain only) */}
      <Route path="/signup" element={
        isMarketingDomain()
          ? <ExternalRedirect to={`${PLATFORM_URL}/signup`} />
          : isPlatformSubdomain()
            ? <OwnerPublicRoute><OwnerSignup /></OwnerPublicRoute>
            : <Navigate to="/register" replace />
      } />
      <Route path="/panels" element={<OwnerRoute><Panels /></OwnerRoute>} />
      <Route path="/order-panel" element={<OwnerRoute><OrderPanel /></OwnerRoute>} />
      <Route path="/owner/child-panels" element={<OwnerRoute><OwnerChildPanels /></OwnerRoute>} />
      <Route path="/owner/gateways" element={<OwnerRoute><OwnerGateways /></OwnerRoute>} />
      <Route path="/owner/parsing-credits" element={<OwnerRoute><OwnerParsingCredits /></OwnerRoute>} />
      <Route path="/owner/hosted-email" element={<OwnerRoute><OwnerHostedEmail /></OwnerRoute>} />
      <Route path="/owner/panels/:tenantId/staff" element={<OwnerRoute><OwnerPanelStaff /></OwnerRoute>} />
      <Route path="/owner/panels/:tenantId/activity" element={<OwnerRoute><OwnerPanelActivity /></OwnerRoute>} />
      <Route path="/owner/panels/:tenantId/admin-api" element={<OwnerRoute><OwnerPanelAdminApi /></OwnerRoute>} />
      <Route path="/owner/panels/:tenantId/domain" element={<OwnerRoute><OwnerPanelDomain /></OwnerRoute>} />
      <Route path="/owner/panels/:tenantId/dns" element={<OwnerRoute><OwnerPanelDns /></OwnerRoute>} />
      <Route path="/owner/panels/:tenantId/change-domain" element={<OwnerRoute><OwnerPanelDomain view="change" /></OwnerRoute>} />
      <Route path="/owner/domains" element={<OwnerRoute><OwnerDomains /></OwnerRoute>} />
      <Route path="/owner/wallet" element={<OwnerRoute><OwnerWallet /></OwnerRoute>} />
      <Route path="/owner/invoices" element={<OwnerRoute><OwnerInvoices /></OwnerRoute>} />
      <Route path="/owner/invoices/:invoiceId" element={<OwnerRoute><OwnerInvoiceDetail /></OwnerRoute>} />
      <Route path="/owner/referrals" element={<OwnerRoute><OwnerReferrals /></OwnerRoute>} />
      <Route path="/owner/support" element={<OwnerRoute><OwnerSupport /></OwnerRoute>} />
      <Route path="/owner/support/:ticketId" element={<OwnerRoute><OwnerSupport /></OwnerRoute>} />
      <Route path="/owner/whats-new" element={<OwnerRoute><OwnerWhatsNew /></OwnerRoute>} />
      <Route path="/owner/settings" element={<OwnerRoute><OwnerSettings /></OwnerRoute>} />
      {/* Owner verification links land on my.*; tenant users keep the original page. */}
      <Route path="/verify-email" element={
        isPlatformSubdomain()
          ? <OwnerVerifyEmail />
          : <PlatformRoute><VerifyEmail /></PlatformRoute>
      } />
      <Route path="/features" element={<PlatformRoute marketingOnly><FeaturesPage /></PlatformRoute>} />
      <Route path="/pricing" element={<PlatformRoute marketingOnly><PricingPage /></PlatformRoute>} />
      <Route path="/user-demo" element={<PlatformRoute marketingOnly><DemoPage key="user-demo" mode="user" /></PlatformRoute>} />
      <Route path="/admin-demo" element={<PlatformRoute marketingOnly><DemoPage key="admin-demo" mode="admin" /></PlatformRoute>} />
      <Route path="/updates" element={<PlatformRoute marketingOnly><UpdatesPage /></PlatformRoute>} />
      <Route path="/blog" element={isMainDomain
        ? <PlatformRoute><BlogPage /></PlatformRoute>
        : <TenantRoute><PublishedContent blog /></TenantRoute>} />
      <Route path="/super-admin" element={
        <PlatformRoute>
          <Navigate
            to={localStorage.getItem('super_admin_token')
              ? '/super-admin/dashboard'
              : '/super-admin/login'}
            replace
          />
        </PlatformRoute>
      } />
      <Route path="/super-admin/login" element={<PlatformRoute><SuperAdminLogin /></PlatformRoute>} />
      <Route path="/super-admin/dashboard" element={<PlatformRoute><SuperAdminDashboard /></PlatformRoute>} />
      <Route path="/super-admin/tenants/:tenantId" element={<PlatformRoute><SuperAdminPanelDetail /></PlatformRoute>} />
      <Route path="/super-admin/tenants/:tenantId/users" element={<PlatformRoute><SuperAdminTenantUsers /></PlatformRoute>} />

      {/* Tenant Auth Routes - wrapped with TenantRoute to check active status */}
      {/* On my.* /login authenticates PLATFORM OWNERS; on tenant subdomains it
          stays the end-user login, untouched. */}
      <Route path="/login" element={
        isMarketingDomain()
          ? <ExternalRedirect to={`${PLATFORM_URL}/login`} />
          : isPlatformSubdomain()
            ? <OwnerPublicRoute><OwnerLogin /></OwnerPublicRoute>
            : <TenantRoute><PublicRoute><Login /></PublicRoute></TenantRoute>
      } />
      {/* On my.* owner signup lives at /signup; on tenant subdomains /register
          stays end-user signup (→ /api/auth/register). */}
      <Route path="/register" element={
        isMarketingDomain()
          ? <ExternalRedirect to={`${PLATFORM_URL}/signup`} />
          : isPlatformSubdomain()
            ? <Navigate to="/signup" replace />
            : <TenantRoute><PublicRoute><Register /></PublicRoute></TenantRoute>
      } />
      <Route path="/forgot-password" element={
        isPlatformSubdomain()
          ? <OwnerForgotPassword />
          : <TenantRoute><PublicRoute><ForgotPassword /></PublicRoute></TenantRoute>
      } />
      <Route path="/reset-password" element={
        isPlatformSubdomain()
          ? <OwnerResetPassword />
          : <TenantRoute><PublicRoute><ResetPassword /></PublicRoute></TenantRoute>
      } />
      {/* /sadmin is a PANEL's admin login, so it only makes sense on a panel's
          own host. TenantRoute passes children through when isMainDomain is
          true, so without this guard the panel admin login also rendered on the
          marketing site and on my.* — two places it can never be used. */}
      <Route path="/sadmin" element={
        isMarketingDomain()
          ? <Navigate to="/" replace />
          : isPlatformSubdomain()
            ? <Navigate to="/login" replace />
            : <TenantRoute><AdminEntryRoute /></TenantRoute>
      } />
      <Route path="/sadmin/forgot-password" element={
        isMarketingDomain()
          ? <Navigate to="/" replace />
          : isPlatformSubdomain()
            ? <Navigate to="/login" replace />
            : <Navigate to="/sadmin" replace />
      } />
      <Route path="/sadmin/reset-password" element={
        isMarketingDomain()
          ? <Navigate to="/" replace />
          : isPlatformSubdomain()
            ? <Navigate to="/login" replace />
            : <Navigate to="/sadmin" replace />
      } />

      {/* Protected User Routes */}
      <Route path="/dashboard" element={<TenantRoute><ProtectedRoute><Dashboard /></ProtectedRoute></TenantRoute>} />
      <Route path="/offers" element={<TenantRoute><ProtectedRoute><PublishedContent fixedSlug="offers" /></ProtectedRoute></TenantRoute>} />
      <Route path="/order" element={<TenantRoute><ProtectedRoute><NewOrder /></ProtectedRoute></TenantRoute>} />
      <Route path="/mass-order" element={<TenantRoute><ProtectedRoute><MassOrder /></ProtectedRoute></TenantRoute>} />
      <Route path="/services" element={isMainDomain ? <Navigate to="/" replace /> : <TenantRoute><Services /></TenantRoute>} />
      <Route path="/drip-feed" element={<TenantRoute><ProtectedRoute><NewOrder dripFeed /></ProtectedRoute></TenantRoute>} />
      <Route path="/orders" element={<TenantRoute><ProtectedRoute><OrderHistory /></ProtectedRoute></TenantRoute>} />
      <Route path="/subscriptions" element={<TenantRoute><ProtectedRoute><Subscriptions /></ProtectedRoute></TenantRoute>} />
      <Route path="/imported-history/orders" element={<TenantRoute><ProtectedRoute><ImportedHistory kind="orders" /></ProtectedRoute></TenantRoute>} />
      <Route path="/imported-history/payments" element={<TenantRoute><ProtectedRoute><ImportedHistory kind="payments" /></ProtectedRoute></TenantRoute>} />
      <Route path="/payments" element={<TenantRoute><ProtectedRoute><Payments /></ProtectedRoute></TenantRoute>} />
      <Route path="/faq" element={<TenantRoute><PublishedContent fixedSlug="faq" /></TenantRoute>} />
      <Route path="/tickets" element={<TenantRoute><ProtectedRoute><Tickets /></ProtectedRoute></TenantRoute>} />
      <Route path="/terms" element={<TenantRoute><PublishedContent fixedSlug="terms" /></TenantRoute>} />
      <Route path="/customer/verify-email" element={<TenantRoute><CustomerVerifyEmail /></TenantRoute>} />
      <Route path="/api-docs" element={<TenantRoute><APIPage /></TenantRoute>} />
      <Route path="/affiliates" element={<TenantRoute><ProtectedRoute><Affiliates /></ProtectedRoute></TenantRoute>} />
      <Route path="/child-panel" element={<TenantRoute><ProtectedRoute><ChildPanel /></ProtectedRoute></TenantRoute>} />
      <Route path="/settings" element={<TenantRoute><ProtectedRoute><Settings /></ProtectedRoute></TenantRoute>} />

      <Route path="/pages/:slug" element={<TenantRoute><PublishedContent /></TenantRoute>} />
      <Route path="/blog/:slug" element={<TenantRoute><PublishedContent blog /></TenantRoute>} />

      {/* Protected Admin Routes — permission mirrors the backend section */}
      <Route path="/sadmin/account" element={<TenantRoute><ProtectedRoute staffOnly><AdminAccount /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/users" element={<TenantRoute><ProtectedRoute permission="users"><AdminUsers /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/users/export" element={<TenantRoute><ProtectedRoute adminOnly><AdminExportUsers /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/services" element={<TenantRoute><ProtectedRoute permission="services"><AdminServices /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/orders" element={<TenantRoute><ProtectedRoute permission="orders"><AdminOrders /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/imported-history/orders" element={<TenantRoute><ProtectedRoute permission="orders.view"><AdminSettingsTools section="imported_history" kind="orders" /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/imported-history/payments" element={<TenantRoute><ProtectedRoute permission="payments.view"><AdminSettingsTools section="imported_history" kind="payments" /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/subscriptions" element={<TenantRoute><ProtectedRoute permission="subscriptions"><AdminSubscriptions /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/refill" element={<TenantRoute><ProtectedRoute permission="refill"><AdminRefill /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/cancel" element={<TenantRoute><ProtectedRoute permission="cancel"><AdminCancel /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/affiliates" element={<TenantRoute><ProtectedRoute permission="affiliates"><AdminAffiliates /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/child-panels" element={<TenantRoute><ProtectedRoute permission="child_panels"><AdminChildPanels /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/drip-feed" element={<TenantRoute><ProtectedRoute permission="dripfeed"><AdminDripFeed /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/providers" element={<TenantRoute><ProtectedRoute permission="providers"><AdminProviders /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/tickets" element={<TenantRoute><ProtectedRoute permission="tickets"><AdminTickets /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/payments" element={<TenantRoute><ProtectedRoute permission="payments"><AdminPayments /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/settings" element={<TenantRoute><ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/settings/providers" element={<TenantRoute><ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/settings/auto-likes" element={<TenantRoute><ProtectedRoute adminOnly><AdminAutoLikesSettings /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/reports" element={<TenantRoute><ProtectedRoute adminOnly><AdminReports /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/migration-tools" element={<TenantRoute><ProtectedRoute adminOnly><AdminSettingsTools section="migration_tools" /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/notifications" element={<TenantRoute><ProtectedRoute adminOnly><AdminSettingsTools section="notifications" /></ProtectedRoute></TenantRoute>} />
      <Route path="/last-updates" element={<TenantRoute><ProtectedRoute><LastUpdates /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/last-updates" element={<TenantRoute><ProtectedRoute staffOnly><AdminLastUpdates /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/offers" element={<TenantRoute><ProtectedRoute permission="appearance.pages"><AdminOffers /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/appearance" element={<TenantRoute><ProtectedRoute permission="appearance"><AdminAppearance /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/appearance/files" element={<TenantRoute><ProtectedRoute permission="appearance.files"><AdminAppearance fixedSection="files" /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/appearance/create-page" element={<TenantRoute><ProtectedRoute permission="appearance"><AdminPageEditor /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/appearance/edit-page/:pageId" element={<TenantRoute><ProtectedRoute permission="appearance"><AdminPageEditor /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/appearance/create-blog" element={<TenantRoute><ProtectedRoute permission="appearance"><AdminBlogEditor /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/appearance/edit-blog/:postId" element={<TenantRoute><ProtectedRoute permission="appearance"><AdminBlogEditor /></ProtectedRoute></TenantRoute>} />
      <Route path="/sadmin/appearance/theme-editor/:themeId" element={<TenantRoute><ProtectedRoute permission="appearance"><AdminThemeEditor /></ProtectedRoute></TenantRoute>} />

      {/* Legacy admin URLs are no longer canonical. Keep old bookmarks safe. */}
      <Route path="/admin/*" element={<Navigate to="/sadmin" replace />} />

      <Route path="/:slug" element={isMainDomain ? <Navigate to="/" replace /> : <TenantRoute><PublishedContent /></TenantRoute>} />

      {/* Default redirect based on domain type */}
      <Route path="*" element={
        isMainDomain ? <Navigate to="/" replace /> : <Navigate to="/login" replace />
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <TenantProvider>
        <PlatformIdentityProvider>
        <AuthProvider>
          {/* Owner sessions are a separate context on a separate storage key;
              nesting keeps tenant auth completely unaffected. */}
          <OwnerAuthProvider>
          <LanguageProvider>
          <TenantCustomerAppearance><AppRoutes /></TenantCustomerAppearance>
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: '#18181b',
                border: '1px solid #27272a',
                color: '#fff',
              },
            }}
          />
          </LanguageProvider>
          </OwnerAuthProvider>
        </AuthProvider>
        </PlatformIdentityProvider>
      </TenantProvider>
    </BrowserRouter>
  );
}

export default App;

